import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cashMovementSchema,
  PAYMENT_METHOD_LABELS,
  type CashSessionDTO,
  type PaymentMethod,
  type PosSettings,
  type SaleRowDTO,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHeader,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { ArrowDownToLine, ArrowUpFromLine, Lock, Printer, ShoppingCart, Wallet } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { SaleDetailDialog } from '../components/SaleDetailDialog';
import { api, errorMessage } from '../lib/api';
import { formatDateTime, formatMoney } from '../lib/format';
import { useSession } from '../lib/session';
import { discardSale, retryFailed, useOffline } from '../lib/offline';
import { printCashSummary } from '../lib/ticket-print';
import { centsToPesosInput, pesosToCents } from '../lib/units';

const BILLS = [20_000, 10_000, 2_000, 1_000, 500, 200, 100, 50, 20, 10];

export function CashPage() {
  const { me, can } = useSession();
  const current = useQuery({
    queryKey: ['cash', 'current'],
    queryFn: () => api.get<CashSessionDTO | null>('/cash/current'),
  });
  const settings = useQuery({
    queryKey: ['pos', 'settings'],
    queryFn: () => api.get<PosSettings>('/pos/settings'),
  });
  const canHistory = can('sales.void') || can('dashboard.view');
  const history = useQuery({
    queryKey: ['cash', 'sessions'],
    queryFn: () => api.get<CashSessionDTO[]>('/cash/sessions'),
    enabled: canHistory,
  });
  const [detail, setDetail] = useState<CashSessionDTO | null>(null);
  const s = current.data;
  const net = useOffline(me.user.id);

  return (
    <>
      {net.failed.length > 0 && (
        <Card>
          <CardHeader
            title="Ventas sin internet que no se pudieron subir"
            subtitle="El sistema las rechazó al subirlas. Revisá el motivo, cargalas a mano si hace falta y después quitalas de esta lista."
          />
          <CardBody>
            {net.failed.map((f) => (
              <div key={f.clientId} className="bf-pos-row" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span>
                  {formatDateTime(f.soldAt)} · {formatMoney(f.totalCents)} · {f.body.items.length} producto{f.body.items.length === 1 ? '' : 's'}
                  <div className="bf-cell-sub bf-text-danger">{f.error}</div>
                </span>
                <Button size="sm" variant="ghost" onClick={() => discardSale(f.clientId)}>
                  Ya la resolví
                </Button>
              </div>
            ))}
            <Button size="sm" onClick={() => void retryFailed()} style={{ marginTop: 8 }}>
              Reintentar
            </Button>
          </CardBody>
        </Card>
      )}
      <PageHeader
        title="Caja"
        subtitle="Efectivo del turno: lo que entró, lo que salió y cuánto tiene que haber en el cajón."
        actions={
          <Link to="/venta" className="bf-btn bf-btn--primary">
            <ShoppingCart /> Ir al punto de venta
          </Link>
        }
      />

      {current.isPending ? (
        <Skeleton style={{ height: 180 }} />
      ) : !s ? (
        <Card>
          <EmptyState
            icon={<Wallet />}
            title="No tenés una caja abierta"
            text="La caja se abre desde el punto de venta, con el efectivo inicial (cambio)."
            action={
              <Link to="/venta" className="bf-btn bf-btn--primary">
                Abrir caja
              </Link>
            }
          />
        </Card>
      ) : (
        <CurrentSession session={s} businessName={me.tenant.name} settings={settings.data} />
      )}

      {canHistory && (
        <Card>
          <CardHeader title="Cajas anteriores" subtitle="Últimos turnos con su arqueo. Tocá uno para ver el detalle." />
          <CardBody flush>
            {history.isPending ? (
              <div style={{ padding: '8px 22px 16px' }}>
                <Skeleton />
              </div>
            ) : (history.data?.length ?? 0) === 0 ? (
              <EmptyState icon={<Wallet />} title="Todavía no hay cajas" />
            ) : (
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Apertura</th>
                      <th>Sucursal</th>
                      <th>Abrió</th>
                      <th>Ventas</th>
                      <th>Esperado</th>
                      <th>Contado</th>
                      <th>Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.data?.map((h) => (
                      <tr key={h.id} data-clickable="true" onClick={() => setDetail(h)}>
                        <td className="bf-cell-sub">
                          {formatDateTime(h.openedAt)} {h.status === 'OPEN' && <Badge tone="success">Abierta</Badge>}
                        </td>
                        <td>
                          {h.branchName}
                          {h.deviceName && <div className="bf-cell-sub">{h.deviceName}</div>}
                        </td>
                        <td>{h.openedByName}</td>
                        <td className="bf-num">
                          {formatMoney(h.salesCents)} <span className="bf-cell-sub">({h.salesCount})</span>
                        </td>
                        <td className="bf-num">{formatMoney(h.expectedCents)}</td>
                        <td className="bf-num">{h.countedCents !== null ? formatMoney(h.countedCents) : '—'}</td>
                        <td>{h.differenceCents === null ? '—' : <Difference cents={h.differenceCents} />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {detail && (
        <Dialog
          open
          onOpenChange={(o) => !o && setDetail(null)}
          title={`Caja de ${detail.branchName}`}
          description={formatDateTime(detail.openedAt)}
          wide
        >
          <SessionSummary session={detail} />
          <div className="bf-form-actions" style={{ marginTop: 16 }}>
            {settings.data && (
              <Button
                icon={<Printer />}
                onClick={() => printCashSummary(detail, settings.data!.ticket.businessName || me.tenant.name, settings.data!.ticket)}
              >
                Imprimir
              </Button>
            )}
            <Button variant="primary" onClick={() => setDetail(null)}>
              Listo
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
}

function Difference({ cents }: { cents: number }) {
  if (cents === 0) return <Badge tone="success">Justo</Badge>;
  return <Badge tone={cents < 0 ? 'danger' : 'warning'}>{cents < 0 ? `Faltan ${formatMoney(-cents)}` : `Sobran ${formatMoney(cents)}`}</Badge>;
}

/** Tarjetas del turno por posnet: para compararlas con el cierre de lote que imprime el posnet. */
function CardsCheck({ session: s }: { session: CashSessionDTO }) {
  if (s.cards.length === 0) return null;
  const byTerminal = new Map<string, CashSessionDTO['cards']>();
  for (const c of s.cards) {
    const k = c.terminal ?? 'Posnet';
    byTerminal.set(k, [...(byTerminal.get(k) ?? []), c]);
  }
  return (
    <div className="bf-cards-check">
      <strong>Control con el posnet</strong>
      <p className="bf-hint" style={{ margin: 0 }}>
        Compará estos totales con el <strong>cierre de lote</strong> que imprime el posnet. Si no coinciden, alguna tarjeta se cobró mal o no se cargó
        en BifeAI.
      </p>
      <table className="bf-table">
        <tbody>
          {[...byTerminal.entries()].map(([t, rows]) =>
            rows.map((c, i) => (
              <tr key={`${t}-${c.method}`}>
                <td className="bf-cell-main">{i === 0 ? t : ''}</td>
                <td>{PAYMENT_METHOD_LABELS[c.method]}</td>
                <td className="bf-cell-sub">
                  {c.count} {c.count === 1 ? 'cupón' : 'cupones'}
                </td>
                <td className="bf-num" style={{ textAlign: 'right', fontWeight: 600 }}>
                  {formatMoney(c.amountCents)}
                </td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </div>
  );
}

function SessionSummary({ session: s }: { session: CashSessionDTO }) {
  const methods = Object.entries(s.byMethod) as [PaymentMethod, number][];
  return (
    <div className="bf-form">
      <dl className="bf-kv">
        <div>
          <dt>Abrió</dt>
          <dd>
            {s.openedByName} · {formatDateTime(s.openedAt)}
          </dd>
        </div>
        {s.closedAt && (
          <div>
            <dt>Cerró</dt>
            <dd>
              {s.closedByName} · {formatDateTime(s.closedAt)}
            </dd>
          </div>
        )}
        <div>
          <dt>Fondo inicial</dt>
          <dd className="bf-num">{formatMoney(s.openingCents)}</dd>
        </div>
        <div>
          <dt>Ventas</dt>
          <dd className="bf-num">
            {formatMoney(s.salesCents)} ({s.salesCount})
          </dd>
        </div>
        {methods.map(([m, v]) => (
          <div key={m}>
            <dt>{PAYMENT_METHOD_LABELS[m]}</dt>
            <dd className="bf-num">{formatMoney(v)}</dd>
          </div>
        ))}
        {s.collectionsCents !== 0 && (
          <div>
            <dt>Cobros de fiado</dt>
            <dd className="bf-num">
              {formatMoney(s.collectionsCents)}
              {(s.collectionsByMethod.CASH ?? 0) !== 0 && ` (efectivo ${formatMoney(s.collectionsByMethod.CASH ?? 0)})`}
            </dd>
          </div>
        )}
        <div>
          <dt>Ingresos / retiros</dt>
          <dd className="bf-num">
            +{formatMoney(s.depositsCents)} / −{formatMoney(s.withdrawalsCents)}
          </dd>
        </div>
        <div>
          <dt>Efectivo esperado</dt>
          <dd className="bf-num">{formatMoney(s.expectedCents)}</dd>
        </div>
        {s.countedCents !== null && (
          <div>
            <dt>Contado</dt>
            <dd className="bf-num">{formatMoney(s.countedCents)}</dd>
          </div>
        )}
        {s.differenceCents !== null && (
          <div>
            <dt>Diferencia</dt>
            <dd>
              <Difference cents={s.differenceCents} />
            </dd>
          </div>
        )}
      </dl>
      <CardsCheck session={s} />
      {s.closeNotes && <Callout>{s.closeNotes}</Callout>}
      {s.drawerOpens.length > 0 && (
        <details className="bf-details">
          <summary>
            Cajón abierto sin venta: <strong>{s.drawerOpens.length}</strong> {s.drawerOpens.length === 1 ? 'vez' : 'veces'}
          </summary>
          <div className="bf-scroll-box">
            <table className="bf-table">
              <tbody>
                {s.drawerOpens.map((d) => (
                  <tr key={d.id}>
                    <td className="bf-cell-sub">{formatDateTime(d.createdAt)}</td>
                    <td>
                      {d.reason}
                      <div className="bf-cell-sub">{d.createdByName}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      {s.movements.length > 0 && (
        <div className="bf-scroll-box">
          <table className="bf-table">
            <tbody>
              {s.movements.map((m) => (
                <tr key={m.id}>
                  <td className="bf-cell-sub">{formatDateTime(m.createdAt)}</td>
                  <td>
                    {m.reason}
                    <div className="bf-cell-sub">{m.createdByName}</div>
                  </td>
                  <td className={`bf-num ${m.type === 'WITHDRAWAL' ? 'bf-text-danger' : 'bf-text-success'}`} style={{ textAlign: 'right' }}>
                    {m.type === 'WITHDRAWAL' ? '−' : '+'}
                    {formatMoney(m.amountCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CurrentSession({ session: s, businessName, settings }: { session: CashSessionDTO; businessName: string; settings?: PosSettings }) {
  const { me, can } = useSession();
  // Con ventas sin internet sin subir, la caja no se cierra: el arqueo daría mal.
  const net = useOffline(me.user.id);
  const blockClose = net.pending > 0 || !net.online;
  const qc = useQueryClient();
  const toast = useToast();
  const [moving, setMoving] = useState<'DEPOSIT' | 'WITHDRAWAL' | null>(null);
  const [closing, setClosing] = useState(false);
  const [saleOpen, setSaleOpen] = useState<string | null>(null);
  const sales = useQuery({
    queryKey: ['sales', 'session', s.id],
    queryFn: () => api.get<SaleRowDTO[]>(`/sales?sessionId=${s.id}`),
    enabled: can('cash.own') || can('sales.void') || can('dashboard.view'),
  });
  const methods = Object.entries(s.byMethod) as [PaymentMethod, number][];
  const refresh = () => Promise.all(['cash', 'pos', 'sales'].map((k) => qc.invalidateQueries({ queryKey: [k] })));

  return (
    <>
      <Card>
        <CardHeader
          title={
            <>
              Caja abierta <Badge tone="success">{s.branchName}</Badge>
            </>
          }
          subtitle={`${s.deviceName ?? 'Caja personal'} · abrió ${s.openedByName} el ${formatDateTime(s.openedAt)}`}
          actions={
            can('cash.own') && (
              <>
                <Button size="sm" icon={<ArrowDownToLine />} onClick={() => setMoving('DEPOSIT')}>
                  Ingreso
                </Button>
                <Button size="sm" icon={<ArrowUpFromLine />} onClick={() => setMoving('WITHDRAWAL')}>
                  Retiro
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  icon={<Lock />}
                  disabled={blockClose}
                  title={blockClose ? 'Primero tienen que subirse las ventas hechas sin internet' : undefined}
                  onClick={() => setClosing(true)}
                >
                  Cerrar caja
                </Button>
              </>
            )
          }
        />
        <CardBody>
          {blockClose && can('cash.own') && (
            <Callout tone="warning">
              {net.online
                ? `Hay ${net.pending === 1 ? '1 venta hecha' : `${net.pending} ventas hechas`} sin internet subiéndose. La caja se puede cerrar cuando terminen.`
                : 'Sin internet no se puede cerrar la caja. Seguí vendiendo: cuando vuelva la conexión, las ventas se suben solas.'}
            </Callout>
          )}
          <div className="bf-kpi-grid">
            <Kpi
              label="Efectivo en el cajón (esperado)"
              value={formatMoney(s.expectedCents)}
              delta="Fondo + ventas y cobros en efectivo + ingresos − retiros"
              deltaMuted
            />
            <Kpi
              label="Ventas del turno"
              tone="green"
              value={formatMoney(s.salesCents)}
              delta={`${s.salesCount} ${s.salesCount === 1 ? 'ticket' : 'tickets'}`}
              deltaMuted
            />
            <Kpi label="Fondo inicial" tone="amber" value={formatMoney(s.openingCents)} delta="Cambio con el que se abrió" deltaMuted />
            <Kpi
              label="Ingresos y retiros"
              tone="red"
              value={`${formatMoney(s.depositsCents - s.withdrawalsCents)}`}
              delta={`+${formatMoney(s.depositsCents)} / −${formatMoney(s.withdrawalsCents)}`}
              deltaMuted
            />
          </div>
          {s.collectionsCents !== 0 && (
            <p className="bf-help" style={{ marginTop: 14 }}>
              <span>
                Cobros de cuenta corriente en este turno: <strong className="bf-num">{formatMoney(s.collectionsCents)}</strong>
                {(s.collectionsByMethod.CASH ?? 0) !== 0 && <> (en efectivo {formatMoney(s.collectionsByMethod.CASH ?? 0)}, ya sumado al cajón)</>}.
              </span>
            </p>
          )}
          {methods.length > 0 && (
            <div className="bf-chips" style={{ marginTop: 16 }}>
              {methods.map(([m, v]) => (
                <span key={m} className="bf-chip" aria-pressed="false">
                  {PAYMENT_METHOD_LABELS[m]}: <strong className="bf-num">{formatMoney(v)}</strong>
                </span>
              ))}
            </div>
          )}
          <CardsCheck session={s} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Tickets de este turno" subtitle="Tocá un ticket para reimprimirlo o anularlo." />
        <CardBody flush>
          {sales.isPending ? (
            <div style={{ padding: '8px 22px 16px' }}>
              <Skeleton />
            </div>
          ) : (sales.data?.length ?? 0) === 0 ? (
            <EmptyState icon={<ShoppingCart />} title="Todavía no hay ventas en este turno" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <tbody>
                  {sales.data?.map((r) => (
                    <tr key={r.id} data-clickable="true" onClick={() => setSaleOpen(r.id)}>
                      <td className="bf-num bf-cell-main">
                        {r.ticket} {r.status === 'VOIDED' && <Badge tone="danger">Anulada</Badge>}
                        {r.refundPending && <Badge tone="warning">Devolución pendiente</Badge>}
                      </td>
                      <td className="bf-cell-sub">{formatDateTime(r.createdAt)}</td>
                      <td>{r.cashierName}</td>
                      <td className="bf-cell-sub">
                        {r.methods.map((m) => PAYMENT_METHOD_LABELS[m]).join(' + ')}
                        {r.customerName && <div>{r.customerName}</div>}
                      </td>
                      <td
                        className="bf-num"
                        style={{
                          textAlign: 'right',
                          fontWeight: 600,
                          textDecoration: r.status === 'VOIDED' ? 'line-through' : undefined,
                        }}
                      >
                        {formatMoney(r.totalCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {moving && (
        <MovementDialog
          type={moving}
          maxCents={moving === 'WITHDRAWAL' ? s.expectedCents : null}
          onClose={() => setMoving(null)}
          onDone={async () => {
            toast.success(moving === 'DEPOSIT' ? 'Ingreso registrado' : 'Retiro registrado');
            setMoving(null);
            await refresh();
          }}
        />
      )}
      {closing && (
        <CloseDialog
          session={s}
          onClose={() => setClosing(false)}
          onClosed={async (closed) => {
            setClosing(false);
            toast.success('Caja cerrada', closed.differenceCents === 0 ? 'El arqueo dio justo.' : undefined);
            if (settings) printCashSummary(closed, settings.ticket.businessName || businessName, settings.ticket);
            await refresh();
          }}
        />
      )}
      {saleOpen && <SaleDetailDialog saleId={saleOpen} onClose={() => setSaleOpen(null)} />}
    </>
  );
}

function MovementDialog({
  type,
  maxCents,
  onClose,
  onDone,
}: {
  type: 'DEPOSIT' | 'WITHDRAWAL';
  maxCents: number | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = cashMovementSchema.safeParse({
      type,
      amountCents: pesosToCents(amount) ?? -1,
      reason,
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá los datos.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/cash/movements', parsed.data);
      await onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={type === 'DEPOSIT' ? 'Ingreso de efectivo' : 'Retiro de efectivo'}
      description={
        type === 'DEPOSIT'
          ? 'Plata que entra al cajón sin ser una venta (ej.: más cambio).'
          : `Plata que sale del cajón (ej.: pago a un proveedor, retiro del dueño).${maxCents !== null ? ` Hay ${formatMoney(maxCents)} en la caja.` : ''}`
      }
    >
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Importe">
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus placeholder="$ 0" />
        </Field>
        <Field label="Motivo">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={type === 'DEPOSIT' ? 'Cambio' : 'Pago al repartidor'} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={busy}>
            Registrar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CloseDialog({
  session,
  onClose,
  onClosed,
}: {
  session: CashSessionDTO;
  onClose: () => void;
  onClosed: (s: CashSessionDTO) => Promise<void>;
}) {
  const [counts, setCounts] = useState<Record<number, string>>({});
  const [coins, setCoins] = useState('');
  const [manual, setManual] = useState('');
  const [useCounter, setUseCounter] = useState(true);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fromCounter = BILLS.reduce((a, b) => a + b * 100 * (Number(counts[b] || 0) || 0), 0) + (pesosToCents(coins || '0') ?? 0);
  const counted = useCounter ? fromCounter : (pesosToCents(manual || '0') ?? 0);
  const diff = counted - session.expectedCents;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const closed = await api.post<CashSessionDTO>('/cash/close', {
        countedCents: counted,
        notes: notes.trim() || null,
      });
      await onClosed(closed);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title="Cerrar caja"
      description="Contá el efectivo del cajón. El sistema compara con lo que debería haber."
      wide
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <CardsCheck session={session} />
        <div className="bf-segmented bf-segmented--text" role="group" aria-label="Forma de contar">
          <button type="button" aria-pressed={useCounter} onClick={() => setUseCounter(true)}>
            Contar billetes
          </button>
          <button type="button" aria-pressed={!useCounter} onClick={() => setUseCounter(false)}>
            Escribir el total
          </button>
        </div>
        {useCounter ? (
          <div className="bf-bills">
            {BILLS.map((b) => (
              <label key={b} className="bf-bill">
                <span>{formatMoney(b * 100)}</span>
                <Input
                  inputMode="numeric"
                  value={counts[b] ?? ''}
                  onChange={(e) =>
                    setCounts((c) => ({
                      ...c,
                      [b]: e.target.value.replace(/\D/g, ''),
                    }))
                  }
                  placeholder="0"
                  aria-label={`Billetes de ${b}`}
                />
              </label>
            ))}
            <label className="bf-bill">
              <span>Monedas y sueltos</span>
              <Input inputMode="decimal" value={coins} onChange={(e) => setCoins(e.target.value)} placeholder="$ 0" />
            </label>
          </div>
        ) : (
          <Field label="Efectivo contado">
            <Input
              inputMode="decimal"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              autoFocus
              placeholder={`$ ${centsToPesosInput(session.expectedCents)}`}
            />
          </Field>
        )}
        <div className="bf-close-summary">
          <div>
            <span>Esperado</span>
            <strong className="bf-num">{formatMoney(session.expectedCents)}</strong>
          </div>
          <div>
            <span>Contado</span>
            <strong className="bf-num">{formatMoney(counted)}</strong>
          </div>
          <div>
            <span>Diferencia</span>
            <Difference cents={diff} />
          </div>
        </div>
        <Field label="Observaciones (opcional)">
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={diff !== 0 ? 'Explicá la diferencia si la conocés' : ''}
          />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Volver
          </Button>
          <Button variant="primary" icon={<Lock />} loading={busy} onClick={() => void submit()}>
            Cerrar caja e imprimir
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
